/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  DateCell,
  Icon,
  Inline,
  Tooltip,
  Typography,
} from '@omnipresentgroup/design-system';
import type { DocumentItem } from '@omnipresentgroup/types';
import {
  type ColumnDef,
  createColumnHelper,
  sortingFns,
} from '@tanstack/react-table';
import { useMemo } from 'react';

import { DocumentActionsCell } from '../components';

export enum DocumentListColumnHeaders {
  title = 'Document name',
  createdAt = 'Date',
  actions = '',
}

export const useDocumentsTableConfig = ({
  columnsOverride,
}: {
  columnsOverride?: ColumnDef<DocumentItem, unknown>[];
}) => {
  const columnHelper = useMemo(() => createColumnHelper<DocumentItem>(), []);

  const columns: ColumnDef<DocumentItem, any>[] = useMemo(
    () => [
      columnHelper.accessor('title', {
        cell: (info) => (
          <Inline align="center">
            <Typography as="span" size="14">
              {info.row.original.title}
            </Typography>
            {info.row.original.description && (
              <Tooltip
                id="document-title-tooltip"
                description={info.row.original.description}
                align="start"
              >
                <Icon icon="Help" size="16" />
              </Tooltip>
            )}
          </Inline>
        ),
        header: () => DocumentListColumnHeaders.title,
        size: 1000,
      }),
      columnHelper.accessor('createdAt', {
        cell: (info) => <DateCell value={info.row.original.createdAt} />,
        header: () => DocumentListColumnHeaders.createdAt,
        sortingFn: sortingFns.datetime,
      }),
      columnHelper.display({
        id: 'actions',
        cell: (info) => (
          <DocumentActionsCell
            actions={info.row.original.actions}
            id={info.row.original.id}
          />
        ),
        header: () => DocumentListColumnHeaders.actions,
        size: 10,
      }),
      ...(columnsOverride ?? []),
    ],
    [columnHelper, columnsOverride],
  );

  return {
    columns,
    columnHelper,
  };
};
